import {
    FacebookAuthProvider,
    GithubAuthProvider,
    GoogleAuthProvider,
    OAuthProvider,
    TwitterAuthProvider,
    type AuthCredential,
    type AuthProvider,
} from 'firebase/auth';
import { SOCIAL_SIGN_IN_PROVIDER_IDS } from '@kaeliux/shared';

/**
 * Social sign-in providers built into Firebase Auth. A button appears once the
 * provider is switched on in the Firebase Console (see `useSignInMethods`).
 */
export enum SocialProvider {
    Google = 'google',
    Facebook = 'facebook',
    Twitter = 'twitter',
    GitHub = 'github',
    Microsoft = 'microsoft',
}

export const SOCIAL_PROVIDER_NAMES: Record<SocialProvider, string> = {
    [SocialProvider.Google]: 'Google',
    [SocialProvider.Facebook]: 'Facebook',
    [SocialProvider.Twitter]: 'X',
    [SocialProvider.GitHub]: 'GitHub',
    [SocialProvider.Microsoft]: 'Microsoft',
};

/** Firebase `providerId` of each provider, as it appears in `User.providerData`. */
export const SOCIAL_PROVIDER_IDS: Record<SocialProvider, string> = SOCIAL_SIGN_IN_PROVIDER_IDS;

export function createAuthProvider(provider: SocialProvider): AuthProvider {
    switch (provider) {
        case SocialProvider.Google: {
            const google = new GoogleAuthProvider();
            google.addScope('profile');
            google.addScope('email');
            return google;
        }
        case SocialProvider.Facebook:
            return new FacebookAuthProvider().addScope('email');
        case SocialProvider.Twitter:
            return new TwitterAuthProvider();
        case SocialProvider.GitHub:
            return new GithubAuthProvider().addScope('user:email');
        case SocialProvider.Microsoft:
            return new OAuthProvider(SOCIAL_PROVIDER_IDS[SocialProvider.Microsoft]);
    }
}

/** The credential a failed popup was carrying, so it can be linked once the player signs in the old way. */
export function credentialFromProviderError(provider: SocialProvider, error: unknown): AuthCredential | null {
    const firebaseError = error as Parameters<typeof GoogleAuthProvider.credentialFromError>[0];
    switch (provider) {
        case SocialProvider.Google:
            return GoogleAuthProvider.credentialFromError(firebaseError);
        case SocialProvider.Facebook:
            return FacebookAuthProvider.credentialFromError(firebaseError);
        case SocialProvider.Twitter:
            return TwitterAuthProvider.credentialFromError(firebaseError);
        case SocialProvider.GitHub:
            return GithubAuthProvider.credentialFromError(firebaseError);
        case SocialProvider.Microsoft:
            return OAuthProvider.credentialFromError(firebaseError);
    }
}
